import {
  Button,
  Container,
  Content,
  Frame,
  Icon,
  Stack,
} from '@kinesis/bungle';
import { useNavigate } from 'react-router-dom';
import { Center } from '@/components/center';
import { useLogoutMutation } from '@/api/login';
import {
  useGetAuditDisclosuresQuery,
  auditDisclosurePathname,
} from '@/api/audits';
import {
  Actions,
  Heading,
  IconSpacer,
  Panel,
  Shared,
  SharedItem,
  SharedItemDetail,
  SharedItemName,
  Text,
} from './no-products.styles';

// Where a login with no licensed products lands: what this account can do,
// rather than a dead end. doorway returns the no-products target whenever the
// licence set is empty and knows nothing about what follows — launch is
// unchanged, only what happens when it arrives here. Gray background (like the
// error pages), not the brand background used for the login/registration flows.
//
// Two things can be true of such an account. It may hold direct grants on
// individual entities — today, disclosures shared with it — which are real
// destinations, read in suit under the `/audits` prefix. And it can always
// reach its own settings, which is why offering only logout was wrong.
const NoProducts = () => {
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();
  const { data: disclosures, isLoading } = useGetAuditDisclosuresQuery();

  // Nothing is rendered until the grants are known: the licensed-nothing copy
  // is wrong for a recipient of a share, and flashing it before their
  // disclosures arrive reads as a dead end they then have to re-read.
  if (isLoading) {
    return (
      <Container>
        <Frame>
          <Content sizing='fill-container' height='100%' background='gray' />
        </Frame>
      </Container>
    );
  }

  const shared = disclosures ?? [];

  return (
    <Container>
      <Frame>
        <Content sizing='fill-container' height='100%' background='gray'>
          <Center>
            <Panel>
              <Stack space='small'>
                {shared.length === 0 && (
                  <IconSpacer>
                    <Icon magnitude='xxlarge' type='exclamation-circle' />
                  </IconSpacer>
                )}
                <Heading>
                  {shared.length === 0
                    ? 'No products available'
                    : 'Shared with you'}
                </Heading>
                <Text>
                  {shared.length === 0 ? (
                    <>
                      Your account isn’t licensed for any products yet. Ask your
                      organisation’s administrator to enable a product, or get
                      in touch with our support team.
                    </>
                  ) : (
                    <>
                      Your account isn’t licensed for any products, but these
                      disclosures have been shared with you.
                    </>
                  )}
                </Text>
                {shared.length > 0 && (
                  <Shared>
                    {shared.map((disclosure) => (
                      <SharedItem
                        key={disclosure.id}
                        href={auditDisclosurePathname(disclosure.id)}
                      >
                        <SharedItemName>{disclosure.name}</SharedItemName>
                        <SharedItemDetail>
                          {disclosure.draft ? 'Draft disclosure' : 'Disclosure'}
                        </SharedItemDetail>
                      </SharedItem>
                    ))}
                  </Shared>
                )}
                <Actions>
                  <Button
                    variant='secondary'
                    onClick={() => {
                      navigate('/settings');
                    }}
                  >
                    Settings
                  </Button>
                  <Button
                    onClick={() => {
                      logout();
                    }}
                  >
                    Log out
                  </Button>
                </Actions>
              </Stack>
            </Panel>
          </Center>
        </Content>
      </Frame>
    </Container>
  );
};

export { NoProducts };
